import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import LogoutIcon from "@mui/icons-material/Logout";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import { useNavigate } from "react-router-dom";
import { baseURL } from "../Config/config";
import SelexSolNavbar from "./CommonC/SelexSolNavbar";

const DesktopUser = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const PRODUCT_DOWNLOADS = {

    "RX10": {
        "product_name": "SelexRX 10",
        "version": "10.0",
        "file_name": "SelexRX10-Setup.exe"
    },

    "CIDCAD": {
        "product_name": "CIDCAD",
        "version": "1.0",
        "file_name": "CIDCAD-Setup.exe"
    },

    "SELEXSTUDIO": {
        "product_name": "SelexStudio",
        "version": "2.8XP",
        "file_name": "SelexStudio-Setup.exe"
    },
    "SELEXMBEXPLORER": {
        "product_name": "SelexMB Explorer",
        "version": "3.0XP",
        "file_name": "SelexMBExplorer-Setup.exe"
    }

}

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const token = sessionStorage.getItem("desktopUserToken");

    if (!token) {
      navigate("/level3/login", {
        replace: true,
      });

      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${baseURL}/desktop-users/my-products`,
        {
          method: "GET",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem("desktopUserToken");
        sessionStorage.removeItem("desktopUser");

        navigate("/level3/login", {
          replace: true,
        });

        return;
      }

      if (!response.ok) {
        throw new Error(
          result.message || "Unable to load products."
        );
      }

      setUsername(result.username || "");
      setProducts(result.products || []);

    } catch (error) {
      console.error("Load products error:", error);

      setError(
        error.message || "Unable to load your products."
      );

    } finally {
      setLoading(false);
    }
  };

 
const handleDownload = (product) => {
  const token = sessionStorage.getItem("desktopUserToken");

  if (!token) {
    navigate("/level3/login");
    return;
  }

  try {
    setError("");

    const downloadUrl = product.download_url;

    if (!downloadUrl) {
      throw new Error("Download link is not available.");
    }

    // Signed GCS URL must be used directly.
    // Do not prepend API_BASE_URL.
    const url = new URL(downloadUrl);

    if (
      url.protocol !== "https:" ||
      url.hostname !== "storage.googleapis.com"
    ) {
      throw new Error("Invalid download URL.");
    }

    // Direct browser download from Google Cloud Storage.
    // No JWT header, fetch(), or Blob conversion needed.
    window.location.assign(url.href);

  } catch (error) {
    console.error("Download error:", error);

    setError(
      error.message || "Unable to download product."
    );
  }
};


  const handleLogout = () => {
    sessionStorage.removeItem("desktopUserToken");
    sessionStorage.removeItem("desktopUser");

    navigate("/level3/login", {
      replace: true,
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#eef3f8",
      }}
    >
      <SelexSolNavbar
        isLoggedIn={
          !!sessionStorage.getItem(
            "desktopUserToken"
          )
        }
      />

      <Box
        sx={{
          width: "min(1200px, calc(100% - 40px))",
          mx: "auto",
          pt: 8,
          pb: 8,
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 30,
                fontWeight: 900,
                color: "#0078c9",
                letterSpacing: "-1.4px",
              }}
            >
              Desktop Product Downloads
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 14,
                color: "#64748b",
              }}
            >
              Download the desktop applications assigned to
              your account.
            </Typography>
          </Box>

          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              border: "1px solid #cbd5e1",
              backgroundColor: "#0078c9",
              px: 2.5,

              "&:hover": {
                backgroundColor: "#0078c9",
              },
            }}
          >
            Logout
          </Button>
        </Box>

        {/* USER DETAILS */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            gap: 2,
            mb: 4,
          }}
        >
          {/* USER CARD */}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              backgroundColor: "#fff",
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.06)",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: "1px",
                color: "#94a3b8",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Logged in user
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: 24,
                fontWeight: 800,
                color: "#1e293b",
              }}
            >
              {username || "-"}
            </Typography>
          </Paper>

          {/* PRODUCT COUNT */}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              backgroundColor: "#fff",
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.06)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  letterSpacing: "1px",
                  color: "#94a3b8",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Products Available
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: 30,
                  fontWeight: 900,
                  color: "#0078c9",
                }}
              >
                {products.length}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, #e0f2fe, #bae6fd)",
              }}
            >
              <Inventory2OutlinedIcon
                sx={{
                  fontSize: 30,
                  color: "#0284c7",
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* ERROR */}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
            }}
          >
            {error}
          </Alert>
        )}

        {/* TABLE */}

        <Paper
          elevation={0}
          sx={{
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.2,
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography
              sx={{
                fontSize: 19,
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              Your Products
            </Typography>
          </Box>

          <Box
            sx={{
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 850,

                "& .MuiTableCell-root": {
                  fontSize: "13px",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f1f5f9",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Product ID
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Product Name
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    File
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Version
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Download .exe
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        py: 8,
                      }}
                    >
                      <CircularProgress
                        size={30}
                        sx={{
                          color: "#0078c9",
                        }}
                      />

                      <Typography
                        sx={{
                          mt: 2,
                          fontSize: 13,
                          color: "#64748b",
                        }}
                      >
                        Loading your products...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        py: 7,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#64748b",
                        }}
                      >
                        No products have been assigned to
                        your account.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow
                      key={product.product_id}
                      hover
                    >
                      <TableCell>
                        <Chip
                          label={product.product_id}
                          size="small"
                          sx={{
                            borderRadius: "6px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#0369a1",
                            backgroundColor: "#e0f2fe",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#1e293b",
                          }}
                        >
                          {product.product_name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#64748b",
                          }}
                        >
                          {product.file_name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {product.version || "-"}
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() =>
                            handleDownload(product)
                          }
                          sx={{
                            borderRadius: "8px",
                            px: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            boxShadow: "none",

                            background:
                              "#0078c9",

                            "&:hover": {
                              boxShadow: "none",

                              background:
                                "linear-gradient(90deg, #095c9c 0%, #1f8bc7 100%)",
                            },
                          }}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DesktopUser;